import { useState, type ReactElement } from 'react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import type {
    InfiniteScrollListFieldPredicateDescriptor,
    InfiniteScrollListFetchPage,
    InfiniteScrollListFetchParams,
} from '@/components/ui/infinite-scroll-list.types';
import { SectionErrorBoundary } from '@/components/ui/section-error-boundary';
import {
    ForensicNetworkDetails,
    ForensicRoleDetails,
    ForensicUserDetails,
} from '@/components/ui/forensic-record-details';
import {
    fetchForensicOrganizationOptionsPage,
    fetchForensicUserOptionsPage,
    ForensicOptionPageSize,
} from '@/lib/forensic-option-fetchers';
import { auditApi } from '@/lib/pillarApi';
import {
    RecentRoleActivitySearchRequestSchema,
    RecentUserActivitySearchRequestSchema,
    TransactionLogPaginationCursorQuerySchema,
    type RecentRoleActivityListItem,
    type RecentRoleActivitySearchRequest,
    type RecentUserActivityListItem,
    type RecentUserActivitySearchRequest,
    type SearchFieldPredicate,
} from '@anchorbase/pillar-sdk';
import { AllRoles, type Role } from '@anchorbase/pillar-sdk';

type RecentActivityView = 'users' | 'roles';

const RecentActivityPageSize: number = 100;

const RoleLabels: Readonly<Record<Role, string>> = {
    SUPERADMIN: 'Superadmin',
    INTERNAL_ADMIN: 'Internal Admin',
    PRINCIPAL: 'Principal',
    ADMIN: 'Admin',
    REP: 'Rep',
};

const UserActivityFieldPredicateDescriptors: readonly InfiniteScrollListFieldPredicateDescriptor[] =
    [
        {
            field: 'user_uuid',
            label: 'User',
            operator: 'in',
            valueType: 'string',
            remoteOptions: {
                pageSize: ForensicOptionPageSize,
                fetchPage: fetchForensicUserOptionsPage,
            },
        },
    ];

const RoleActivityFieldPredicateDescriptors: readonly InfiniteScrollListFieldPredicateDescriptor[] =
    [
        ...UserActivityFieldPredicateDescriptors,
        {
            field: 'organization_uuid',
            label: 'Organization',
            operator: 'in',
            valueType: 'string',
            remoteOptions: {
                pageSize: ForensicOptionPageSize,
                fetchPage: fetchForensicOrganizationOptionsPage,
            },
        },
        {
            field: 'role',
            label: 'Role',
            operator: 'in',
            valueType: 'string',
            options: AllRoles.map((role: Role) => ({
                value: role,
                label: RoleLabels[role],
            })),
        },
    ];

interface ActivityViewSliderProps {
    readonly value: RecentActivityView;
    readonly onChange: (value: RecentActivityView) => void;
}

interface UserActivityRowProps {
    readonly item: RecentUserActivityListItem;
}

interface RoleActivityRowProps {
    readonly item: RecentRoleActivityListItem;
}

function parseOpaqueActivityCursor(pageParam: unknown): string | undefined {
    return TransactionLogPaginationCursorQuerySchema.parse(pageParam);
}

function findInclusionPredicateValues(
    fieldPredicates: ReadonlyArray<SearchFieldPredicate> | undefined,
    field: string
): SearchFieldPredicate['values'] {
    const matchingPredicates: SearchFieldPredicate[] = (fieldPredicates ?? []).filter(
        (predicate: SearchFieldPredicate) => predicate.field === field
    );
    if (matchingPredicates.length > 1) {
        throw new Error(`Expected one inclusion predicate for ${field}`);
    }
    const predicate: SearchFieldPredicate | undefined = matchingPredicates[0];
    if (predicate === undefined) {
        return undefined;
    }
    if (predicate.operator !== 'in' || predicate.values === undefined) {
        throw new Error(`Expected an inclusion predicate for ${field}`);
    }
    return predicate.values;
}

export function buildRecentUserActivityRequest(
    params: InfiniteScrollListFetchParams<string>
): RecentUserActivitySearchRequest {
    return RecentUserActivitySearchRequestSchema.parse({
        limit: params.limit,
        cursor: params.cursor,
        user_uuids: findInclusionPredicateValues(
            params.fieldPredicates,
            'user_uuid'
        ),
    });
}

export function buildRecentRoleActivityRequest(
    params: InfiniteScrollListFetchParams<string>
): RecentRoleActivitySearchRequest {
    return RecentRoleActivitySearchRequestSchema.parse({
        limit: params.limit,
        cursor: params.cursor,
        user_uuids: findInclusionPredicateValues(
            params.fieldPredicates,
            'user_uuid'
        ),
        organization_uuids: findInclusionPredicateValues(
            params.fieldPredicates,
            'organization_uuid'
        ),
        roles: findInclusionPredicateValues(params.fieldPredicates, 'role'),
    });
}

const fetchRecentUserActivityPage: InfiniteScrollListFetchPage<
    RecentUserActivityListItem,
    string
> = async (params) => {
    const response = await auditApi.listRecentUserActivityPage(
        buildRecentUserActivityRequest(params)
    );
    return { items: response.activity, pagination: response.pagination };
};

const fetchRecentRoleActivityPage: InfiniteScrollListFetchPage<
    RecentRoleActivityListItem,
    string
> = async (params) => {
    const response = await auditApi.listRecentRoleActivityPage(
        buildRecentRoleActivityRequest(params)
    );
    return { items: response.activity, pagination: response.pagination };
};

function ActivityViewSlider({ value, onChange }: ActivityViewSliderProps): ReactElement {
    return (
        <div
            role="radiogroup"
            aria-label="Recent activity view"
            className="inline-flex rounded-full border bg-white p-1"
        >
            <label
                className={`cursor-pointer rounded-full px-4 py-2 text-sm font-medium ${
                    value === 'users' ? 'bg-primary text-primary-foreground' : 'text-gray-700'
                }`}
            >
                <input
                    type="radio"
                    name="recent-activity-view"
                    value="users"
                    checked={value === 'users'}
                    className="sr-only"
                    onChange={() => onChange('users')}
                />
                User activity
            </label>
            <label
                className={`cursor-pointer rounded-full px-4 py-2 text-sm font-medium ${
                    value === 'roles' ? 'bg-primary text-primary-foreground' : 'text-gray-700'
                }`}
            >
                <input
                    type="radio"
                    name="recent-activity-view"
                    value="roles"
                    checked={value === 'roles'}
                    className="sr-only"
                    onChange={() => onChange('roles')}
                />
                Role activity
            </label>
        </div>
    );
}

function UserActivityRow({ item }: UserActivityRowProps): ReactElement {
    return (
        <article className="space-y-2 rounded-md border bg-white p-3">
            <div>
                <div className="text-sm font-semibold text-gray-900">{item.operation}</div>
                <div className="text-xs text-gray-600">
                    {item.ts} · Activity {item.activity_ref} · Session{' '}
                    {item.session_ref ?? 'Unavailable'}
                </div>
            </div>
            <ForensicUserDetails label="User" reference={item.user} />
            <ForensicRoleDetails roleAssignment={item.effective_role_assignment} />
            <ForensicNetworkDetails
                ipAddress={item.ip_address}
                userAgent={item.user_agent}
            />
        </article>
    );
}

function RoleActivityRow({ item }: RoleActivityRowProps): ReactElement {
    return (
        <article className="space-y-2 rounded-md border bg-white p-3">
            <div>
                <div className="text-sm font-semibold text-gray-900">{item.operation}</div>
                <div className="text-xs text-gray-600">
                    {item.ts} · Activity {item.activity_ref} · Session{' '}
                    {item.session_ref ?? 'Unavailable'}
                </div>
            </div>
            <ForensicUserDetails label="Actor" reference={item.actor} />
            <ForensicRoleDetails roleAssignment={item.effective_role_assignment} />
            <ForensicNetworkDetails
                ipAddress={item.ip_address}
                userAgent={item.user_agent}
            />
        </article>
    );
}

function RecentUserActivityList(): ReactElement {
    return (
        <InfiniteScrollList<RecentUserActivityListItem, string, readonly ['recentActivity', 'users']>
            pageTitle="Recent Activity"
            cardTitle="User activity"
            queryPlaceholder=""
            queryKeyPrefix={['recentActivity', 'users']}
            pageSize={RecentActivityPageSize}
            fetchPage={fetchRecentUserActivityPage}
            parseCursorParam={parseOpaqueActivityCursor}
            showPageHeader={false}
            showQueryInput={false}
            controlsPlacement="above-search"
            predicateControls={{
                fieldPredicates: UserActivityFieldPredicateDescriptors,
            }}
            stateCopy={{
                loadingLabel: 'Loading user activity...',
                emptyLabel: 'No user activity found.',
                genericErrorLabel: 'Failed to load user activity.',
            }}
            renderItems={({ items }) => (
                <div className="space-y-3">
                    {items.map((item: RecentUserActivityListItem) => (
                        <UserActivityRow key={item.activity_ref} item={item} />
                    ))}
                </div>
            )}
        />
    );
}

function RecentRoleActivityList(): ReactElement {
    return (
        <InfiniteScrollList<RecentRoleActivityListItem, string, readonly ['recentActivity', 'roles']>
            pageTitle="Recent Activity"
            cardTitle="Role activity"
            queryPlaceholder=""
            queryKeyPrefix={['recentActivity', 'roles']}
            pageSize={RecentActivityPageSize}
            fetchPage={fetchRecentRoleActivityPage}
            parseCursorParam={parseOpaqueActivityCursor}
            showPageHeader={false}
            showQueryInput={false}
            controlsPlacement="above-search"
            predicateControls={{
                fieldPredicates: RoleActivityFieldPredicateDescriptors,
            }}
            stateCopy={{
                loadingLabel: 'Loading role activity...',
                emptyLabel: 'No role activity found.',
                genericErrorLabel: 'Failed to load role activity.',
            }}
            renderItems={({ items }) => (
                <div className="space-y-3">
                    {items.map((item: RecentRoleActivityListItem) => (
                        <RoleActivityRow key={item.activity_ref} item={item} />
                    ))}
                </div>
            )}
        />
    );
}

export function RecentActivityContent(): ReactElement {
    const [activityView, setActivityView] = useState<RecentActivityView>('users');

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <h1 className="text-3xl font-bold">Recent Activity</h1>
                <ActivityViewSlider value={activityView} onChange={setActivityView} />
            </div>
            {activityView === 'users' ? <RecentUserActivityList /> : <RecentRoleActivityList />}
        </div>
    );
}

export default function RecentActivityPage(): ReactElement {
    return (
        <ProtectedRoute>
            <MainLayout>
                <SectionErrorBoundary sectionName="Recent activity">
                    <RecentActivityContent />
                </SectionErrorBoundary>
            </MainLayout>
        </ProtectedRoute>
    );
}
